import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { OWNERSHIP_CHECK_FAILED } from '../../constants/ErrorCauses';
import { FaqsConfig } from '../../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../../constants/routing/NavItems';
import Screens from '../../constants/routing/Screens';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { usePageParams } from '../../hooks/usePageParams';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { useSSM0004ExperimentEffect } from '../../utils/experiments/ssm0004';
import BannerSection from '../banners/BannerSection';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import IntegrationsSection from '../common/Integrations';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import PhoneSupport from '../common/PhoneSupport';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import ProductInfoContainer from '../common/ProductInfoContainer';
import ValuePropsSection from '../common/ValuePropsSection';
import CompareStatusFooter from '../compare/CompareStatusFooter';
import ErrorPage from '../error-page/ErrorPage';
import Faq from '../faqs/Faq';
import FooterMicrocopy from '../faqs/FooterMicrocopy';
import Question from '../faqs/Question';
import SuiteFeatureTable from '../feature-table/suite-feature-table/SuiteFeatureTable';
import WhyGoHubSpotSection from '../free-tools-page/WhyGoHubSpotSection';
import HeaderLayout from '../layout/HeaderLayout';
import { SuiteTiles } from '../merch-card/SuiteTiles';
import ProductPageSubtitle from '../product-pages/ProductPageSubtitle';
import ProductPageTitle, { ProductPageHeader } from '../product-pages/ProductPageTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SuitePage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('crmSuite');
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const {
    tab
  } = usePageParams();
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: Screens.SUITE,
      tab
    });
  }, [tab]);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    isSSM0004Loading
  } = useSSM0004ExperimentEffect();
  const marketingHighlightedProducts = getHighlightsForProductLine(ProductLines.MARKETING);
  const filteredMarketingHighlightedProducts = marketingHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const salesHighlightedProducts = getHighlightsForProductLine(ProductLines.SALES);
  const filteredSalesHighlightedProducts = salesHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const serviceHighlightedProducts = getHighlightsForProductLine(ProductLines.SERVICE);
  const filteredServiceHighlightedProducts = serviceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const operationsHighlightedProducts = getHighlightsForProductLine(ProductLines.OPERATIONS);
  const filteredOperationsHighlightedProducts = operationsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const cmsHighlightedProducts = getHighlightsForProductLine(ProductLines.CMS);
  const filteredCmsHighlightedProducts = cmsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const productsByProductLine = {
    [ProductLines.MARKETING]: filteredMarketingHighlightedProducts,
    [ProductLines.SALES]: filteredSalesHighlightedProducts,
    [ProductLines.SERVICE]: filteredServiceHighlightedProducts,
    [ProductLines.OPERATIONS]: filteredOperationsHighlightedProducts,
    [ProductLines.CMS]: filteredCmsHighlightedProducts
  };
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }

  // Only true in-app to prevent from showing term toggle / monthly price for split second while experiment cohort is being assigned
  if (isSSM0004Loading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: NavItemEnum.SUITE,
      children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
        children: [/*#__PURE__*/_jsx(BannerSection, {
          screen: Screens.SUITE,
          navItem: NavItemEnum.SUITE
        }), /*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: NavItemEnum.SUITE
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: NavItemEnum.SUITE
          })]
        }), /*#__PURE__*/_jsx(SuiteTiles, {
          selectedTab: tab
        }), /*#__PURE__*/_jsx(SuiteFeatureTable, {
          productsByProductLine: productsByProductLine,
          screen: Screens.SUITE,
          tab: tab
        }, tab), /*#__PURE__*/_jsx(PhoneSupport, {
          phoneSourceId: "crmsuite-middle",
          screen: Screens.SUITE
        }), /*#__PURE__*/_jsx(WhyGoHubSpotSection, {
          selectedNavItem: NavItemEnum.SUITE
        }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
          publicComponentProps: {
            tab
          }
        }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsxs(Faq, {
          children: [FaqsConfig[NavItemEnum.SUITE].map(_ref => {
            let {
                questionKey
              } = _ref,
              passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
            return /*#__PURE__*/_jsx(Question, Object.assign({
              questionKey: questionKey
            }, passthroughOptions), questionKey);
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "crmsuite-footer",
          screen: Screens.SUITE
        })]
      })
    }), /*#__PURE__*/_jsx(CompareStatusFooter, {})]
  });
};
export default SuitePage;